/**
 * Tree Navigation  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import { Type, defineElementaryComponent } from "@adapptio/lib-blueprint";

import {
	ClassList,
	getStringEnumValue,
	TViewComponentDefinition,
	TComponentReact
} from "@adapptio/lib-components";
import { BaseEvents } from "../../base/BaseEvents";
import { termsEditor } from "../../terms";
import { ComponentTreeNavigation_Props } from "./props";
import { TreeNavigationList } from "./Content";
import { TREE_NAVIGATION_STYLE, TREE_NAVIGATION_STYLE_default } from "../../Enums/TREE_NAVIGATION_STYLE";

interface ComponentTreeNavigation_State {}

const ComponentTreeNavigation_Events = {
	...BaseEvents
};

const ComponentTreeNavigation_Definition = defineElementaryComponent<
	typeof ComponentTreeNavigation_Props,
	ComponentTreeNavigation_State,
	typeof ComponentTreeNavigation_Events
>({
	...termsEditor.components.treeNavigation.component,

	name: "treeNavigation",

	category: "navigation",

	icon: "mdi/file-tree",

	docUrl: "...",

	order: 10,

	props: ComponentTreeNavigation_Props,

	events: ComponentTreeNavigation_Events,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentTreeNavigation_React: TComponentReact<typeof ComponentTreeNavigation_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const { items, expandable } = props;

	const componentPath = componentInstance.safePath;

	const { classList } = ClassList.getElementClassListAndIdClassName("cmp-tree-navigation", componentPath, {
		componentInstance,
		componentClassList: reactComponentClassList
	});

	const styleValue = getStringEnumValue(TREE_NAVIGATION_STYLE, props.style, TREE_NAVIGATION_STYLE_default);

	classList.addModifiers({
		style: styleValue,
		expandable: !!expandable
	});

	return (
		<div className={classList.toClassName()}>
			<TreeNavigationList
				items={items}
				expandable={expandable}
				styleValue={styleValue}
				componentPath={componentPath}
				componentMode={componentInstance.componentMode}
			/>
		</div>
	);
};

export const ComponentTreeNavigation: TViewComponentDefinition<
	typeof ComponentTreeNavigation_Definition
> = {
	...ComponentTreeNavigation_Definition,
	reactComponent: ComponentTreeNavigation_React
};
